import { DbRef, UNKNOWN_SCHEDULING_USER, Role } from '@caresend/types';
import update from 'immutability-helper';
import { getPath } from './path.js';
import '../typeValidation/booking.js';
import { isNullish } from '../typeValidation/nullish.js';
import '../typeValidation/draftWaypoint.js';
import '../typeValidation/feedback.js';
import '../environment/crypto/index.js';
import 'dayjs';
import 'lodash-unified';
import '../formatters/string.js';
import 'ngeohash';
import '../formatters/location.js';
import '../initializers/user.js';
import '../typeValidation/note.js';
import '../typeValidation/procedure.js';
import '../typeValidation/samples.js';
import '../typeValidation/shift.js';
import '../typeValidation/shipmentInfo.js';
import '../typeValidation/supply.js';
import '../typeValidation/timeSlot.js';
import '../typeValidation/waypoint.js';
import '../typeValidation/waypointAction.js';

/**
 * ```
 * `${DbRef.USERS}/${id}/${childPath}`
 * ```
 */

function getUserPath(id, childPath) {
  return getPath(`${DbRef.USERS}/${id}`, childPath);
}
const getUserHasPlaceRole = (userPlaces, placeID, role) => {
  var _a;

  return !!((_a = userPlaces === null || userPlaces === void 0 ? void 0 : userPlaces[placeID]) === null || _a === void 0 ? void 0 : _a.placeRoles[role]);
};
/**
 * Given the needed arguments, returns a `UserPlace` which can be set at
 * `${DbRef.USERS}/${id}/places/${placeID}`, either as a new object or replacing
 * the existing one (if the user already has a different role for that place)
 */

const addUserPlaceRole = (userPlaces, placeID, role) => {
  var _a;

  const userPlace = (_a = userPlaces === null || userPlaces === void 0 ? void 0 : userPlaces[placeID]) !== null && _a !== void 0 ? _a : {
    id: placeID,
    placeRoles: {}
  };
  const updatedUserPlace = update(userPlace, {
    placeRoles: {
      [role]: {
        $set: true
      }
    }
  });
  return updatedUserPlace;
};
const shouldEncryptSchedulingUserName = schedulingUserName => schedulingUserName.toLowerCase() !== UNKNOWN_SCHEDULING_USER.toLowerCase();
var AddPatientEntityType;

(function (AddPatientEntityType) {
  AddPatientEntityType["PATIENT"] = "patient";
  AddPatientEntityType["CONTACT"] = "contact";
})(AddPatientEntityType || (AddPatientEntityType = {}));
/**
 * Get filters for patient or scheduling contact matching
 *
 * Throws if missing required fields:
 * - DOB and gender required for patients
 * - email or phone required for contacts
 */


const getFacetFiltersForPatientMatching = (data, entityType) => {
  const {
    firstName,
    lastName,
    dateOfBirth,
    gender,
    email,
    phone
  } = data;
  const roleFilter = `role:${Role.PATIENT}`;
  const firstNameFilter = `info.firstName:${firstName}`;
  const lastNameFilter = `info.lastName:${lastName}`;
  const dobFilter = !isNullish(dateOfBirth) ? `info.dateOfBirth:${dateOfBirth}` : null;
  const genderFilter = gender ? `info.gender:${gender}` : null;
  const emailFilter = email ? `info.email:${email}` : null;
  const phoneFilters = phone ? [`info.phone.number:${phone === null || phone === void 0 ? void 0 : phone.number}`, `info.phone.callingCode:${phone === null || phone === void 0 ? void 0 : phone.callingCode}`] : null;
  const facetFilters = [roleFilter];

  if (entityType === AddPatientEntityType.PATIENT) {
    if (!dobFilter) throw Error('DOB is required for patient matching');
    if (!genderFilter) throw Error('Gender is required for patient matching');
    facetFilters.push(firstNameFilter, lastNameFilter, dobFilter, genderFilter);
  } else {
    if (!emailFilter && !phoneFilters) {
      throw Error('Email or phone is required for contact matching');
    }

    if (emailFilter) {
      facetFilters.push(emailFilter);
    } else {
      facetFilters.push(firstNameFilter, lastNameFilter);
    }

    if (phoneFilters) facetFilters.push(...phoneFilters);
  }

  return facetFilters;
};

export { AddPatientEntityType, addUserPlaceRole, getFacetFiltersForPatientMatching, getUserHasPlaceRole, getUserPath, shouldEncryptSchedulingUserName };
